import React, { useEffect, useId, useState } from 'react';
import { ArrowDown, X } from 'lucide-react';
import { Reveal } from '../Reveal';
import { Eyebrow } from './SectionBand';
import { DeviceFrame } from './DeviceFrame';
const readerContext = ['Known reader interest', 'What matters to them', 'Where they are stuck', 'Actions they have taken', 'What kind of support may fit'];
const authorEnablement = ['Stronger sales conversations', 'Podcast talking points', 'Speaking and webinar assets', 'Workshop ideas', 'Follow-up assets', 'Content angles', 'Clearer offer paths'];
const CONSOLE_SRC = "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/4wv71qfbbbwbngbvre9mbk/69afe213-836a-4c50-a8ec-07cb573626b4/08_author_console_lead_detail.jpg";
const CONSOLE_LABEL = 'Simplify to Amplify · Author console';
const CONSOLE_FRAME = 'Reader activity becomes author-side context you can actually use.';
function ConsoleLightbox({
  onClose
}: {
  onClose: () => void;
}) {
  const titleId = useId();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return <div className="fixed inset-0 z-[70] flex items-center justify-center bg-navy/80 p-4 backdrop-blur-sm md:p-8" role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={onClose} data-id="element-568" data-mp="z8bq468ygude">
      <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-paper shadow-[0_40px_80px_-20px_rgba(0,0,0,0.5)]" onClick={e => e.stopPropagation()} data-id="element-569" data-mp="qg9ssgu6nu7c">
        <div className="sticky top-0 z-10 flex flex-shrink-0 items-start justify-between gap-4 border-b border-gray-200/80 bg-paper px-5 py-5 md:px-6 md:py-6" data-id="element-570" data-mp="gi2mqt1fmwk4">
          <div className="min-w-0 max-w-3xl pr-2" data-id="element-571" data-mp="bjiz8o76xcpn">
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-text-muted" data-id="element-572" data-mp="1vn8k1ha62d3">
              Start the next conversation with context
            </p>
            <p id={titleId} className="mt-2.5 font-serif text-xl leading-snug text-seaglass md:text-[1.65rem]" data-id="element-573" data-mp="6sdn2kdfspwe">
              {CONSOLE_FRAME}
            </p>
          </div>
          <button type="button" onClick={onClose} className="flex-shrink-0 rounded-full p-2 text-text-muted transition-colors hover:bg-paper-dark hover:text-navy focus:outline-none focus:ring-2 focus:ring-seaglass" aria-label="Close preview" data-id="element-574" data-mp="3t26otw5vykis">
            <X className="h-5 w-5" data-id="element-575" data-mp="btjhqnqnqn8k" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-paper px-3 py-3 md:px-5 md:py-5" data-id="element-576" data-mp="btb2wqaz57uq">
          <img src={CONSOLE_SRC} alt="Author console — reader activity becomes author-side context" className="mx-auto block h-auto w-full max-w-full rounded-lg" data-id="element-577" data-mp="fss7lhiku0yu" />
        </div>
      </div>
    </div>;
}
export function AuthorLeverage() {
  const [enlarged, setEnlarged] = useState(false);
  return <section className="bg-navy px-6 py-20 text-paper md:px-10 md:py-28" data-id="element-578" data-mp="2szrvdj4c6eh1">
      <div className="mx-auto max-w-6xl" data-id="element-579" data-mp="2o200qhcb4e95">
        <div className="max-w-3xl" data-id="element-580" data-mp="32ro55rfsbt13">
          <Reveal data-id="element-581" data-mp="32qmhmnhl1e2h">
            <Eyebrow data-id="element-582" data-mp="32r4mg8ib90m0">06 — Start the next conversation with context</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl leading-[1.08] text-paper md:text-5xl" data-id="element-583" data-mp="2xsbawlml3joh">
              Know what mattered before the conversation starts.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-paper/70" data-id="element-584" data-mp="2545l3kgob4hs">
              The author can see the reader’s pattern, saved move, support
              interest, and activity—so follow-up is more relevant and the right
              offer is easier to identify.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid items-start gap-10 lg:grid-cols-[1.2fr_1fr] lg:gap-14" data-id="element-585" data-mp="3vfk0ostmbewa">
          <Reveal delay={0.1} data-id="element-586" data-mp="3qhryn5z9eced">
            <button type="button" onClick={() => setEnlarged(true)} className="group w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass focus-visible:ring-offset-2 focus-visible:ring-offset-navy" aria-label="Enlarge: Author console" data-id="element-587" data-mp="3qggwfalgpcpd">
              <span className="block transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:shadow-[0_28px_50px_-28px_rgba(0,0,0,0.55)]" data-id="element-588" data-mp="290bblksdfjmk">
                <DeviceFrame variant="browser" src={CONSOLE_SRC} alt="Author console — reader activity becomes author-side context" chromeLabel={CONSOLE_LABEL} aspect="16 / 10" data-id="element-589" data-mp="2dxth1176jgpt" />
              </span>
              <span className="mt-3 block text-center text-sm font-medium text-paper/55 transition-colors group-hover:text-paper/80" data-id="element-590" data-mp="2nvzhrfh92gt3">
                Click to enlarge
              </span>
            </button>
          </Reveal>

          <Reveal delay={0.15} data-id="element-591" data-mp="31pqqiln5pwnr">
            <div data-id="element-592" data-mp="31qtfgo0x5p11">
              <div className="rounded-2xl border border-white/12 bg-white/[0.04] p-6" data-id="element-593" data-mp="31rbkoa5k7dl3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-seaglass-light" data-id="element-594" data-mp="36pd08l3s0urt">
                  Reader context
                </p>
                <div className="mt-3 flex flex-wrap gap-2" data-id="element-595" data-mp="3btox9hsvl528">
                  {readerContext.map(item => <span key={item} className="rounded-lg border border-white/12 bg-navy-light/40 px-3 py-1.5 text-sm text-paper/85" data-id="element-596" data-mp="36ucf3acy8m5z">
                      {item}
                    </span>)}
                </div>
              </div>

              <div className="flex flex-col items-center py-3" data-id="element-597" data-mp="3dvjhapxi7ov">
                <ArrowDown className="h-5 w-5 text-seaglass-light" data-id="element-598" data-mp="3ujwx9r1a3fe7" />
                <span className="mt-1 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-paper/45" data-id="element-599" data-mp="3ukot92ysff50">
                  which becomes
                </span>
              </div>

              <div className="rounded-2xl border-2 border-seaglass/50 bg-white/[0.06] p-6 shadow-[0_24px_50px_-30px_rgba(0,0,0,0.6)]" data-id="element-600" data-mp="3lqtklkygj6km">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sand" data-id="element-601" data-mp="m5y5l1lcag6i">
                  Author enablement
                </p>
                <div className="mt-4 grid gap-x-6 gap-y-2.5 sm:grid-cols-2" data-id="element-602" data-mp="2bma5v9kp9vh1">
                  {authorEnablement.map(item => <div key={item} className="flex items-start gap-2.5 text-paper/90" data-id="element-603" data-mp="2gj0kdsguwj0c">
                      <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sand" data-id="element-604" data-mp="21ue6lv6vnzb8" />
                      <span className="leading-snug" data-id="element-605" data-mp="26tqu8i5kpaev">{item}</span>
                    </div>)}
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <p className="mx-auto mt-12 max-w-2xl text-center text-base text-paper/55" data-id="element-606" data-mp="1xudfb9yojtmr">
          <span className="block" data-id="element-607" data-mp="1iwl3osw97pdu">
            Client experience shown to demonstrate the reader and author path.
          </span>
          <span className="mt-1 block" data-id="element-608" data-mp="1nuv0f7xmmy1k">
            No performance outcomes are represented.
          </span>
        </p>
      </div>

      {enlarged && <ConsoleLightbox onClose={() => setEnlarged(false)} data-id="element-609" data-mp="3d8hwap6fn1ah" />}
    </section>;
}